import React, { useRef, useCallback, useState, Component } from 'react';
import {getIsSelected} from './selectors.js';
import {quantizeStep} from './quantizeStep.js';
import {useScroll} from './useScroll.js';
import RollComponent from './RollComponent.js';
import {getBarWidth} from './getBarWidth.js';
import RowSelection from './RowSelection.js';
import MouseHelper from './MouseHelper.js';
import {getStepFromPosition} from './getStepFromPosition.js';
import {insertRollEventAtCursor} from './cursor.js';
import {
    setSelectedArrangementEvents,
    setArrangementSelectedRows,
    setArrangementBottomMode,
    setArrangementRowSelections,
} from 'actions/index.js';
import {moveRollEvent} from 'session/operations/actions.js';
import { connect } from "react-redux";
import {
    NO_SUB_SEQUENCE,
    PATTERN_SWITCH,
    SEQUENCE_ROLL,
    RESOLUTION_CHANGE} from 'session/constants.js';
import {DEFAULT_RESOLUTION} from './constants.js';

const RIGHT = 'RIGHT';
const DOUBLE_CLICK_THRESHOLD = 250;
const LEFT = 'LEFT';

const RollsArrangementComponent = (props) => {
    const getState = () => {
        return props.sequencerEngine.store.getState();
    };

    const getEvents = () => {
        let mixEvents = getState().arrangement.mixEvents[NO_SUB_SEQUENCE.id];
        return mixEvents ? mixEvents[SEQUENCE_ROLL] || [] : [];
    };

    const {visibleView} = useScroll({
        ref: props.wrapperRef,
        resolution: props.resolution,
        xScale: props.xScale,
        events: getEvents(),
        sequencerEngine: props.sequencerEngine,
        widthExtension: 250
    });

    const resizingSelection = useRef(false);
    const lastClickTime = useRef(undefined);
    const resizing = useRef(undefined);
    const resizingSubEvent = useRef(undefined);
    const rollDragging = useRef(undefined);
    const rollDragOffset = useRef(undefined);
    const direction = useRef(undefined);

    const getMouseX = (e) => {
        return e.pageX + props.wrapperRef.current.scrollLeft;
    };

    const onDragPatternEvent = (e, event) => {
    };

    const onSelect = (event, subEvent, e) => {
        if (isSelected(subEvent)) {
            props.setSelectedEvents(undefined);
        } else {
            props.setArrangementRowSelections({});
            let selectedEvents = {};
            selectedEvents[subEvent.id] = subEvent;
            props.setSelectedEvents(selectedEvents);
        }
        rollDragOffset.current = getStepFromMouseEvent(e) - event.step;
        rollDragging.current= event;
    };

    const isSelected = (event) => {
        return selectedEvents() === undefined ? false :
            selectedEvents()[event.id] !== undefined;
    };

    const selectedEvents = () => {
        let state = getState();
        return state.arrangement.selectedEvents;
    };

    const xScale = () => {
        return getState().arrangement.xScale;
    };

    const mix = props.sequencerEngine.mix;

    const onMouseUp = (e) => {
        if (e === undefined) {
            let currentClickTime = new Date().getTime();

            if (lastClickTime.current !== undefined &&
                currentClickTime - lastClickTime.current < DOUBLE_CLICK_THRESHOLD) {
                insertRollEventAtCursor(mix, props.sequencerEngine.store);
            }
            lastClickTime.current= currentClickTime;
        }
        resizing.current = undefined;
        resizingSubEvent.current = undefined;
        resizingSelection.current = false;
        rollDragging.current = undefined;
        rollDragOffset.current = undefined;
    };

    const scrollLeft = () => {
        return props.wrapperRef.current.scrollLeft;
    };

    const getStepFromMouseEvent = useCallback((e) => {
        let x = e.pageX + scrollLeft();
        let step = getStepFromPosition(
            x,
            props.resolution,
            undefined,
            xScale());
        return step;
    }, [props.resolution]);

    const onMouseMove = useCallback((e) => {
        if (resizing.current || rollDragging.current || resizingSelection.current) {
            let x = e.pageX + scrollLeft();
            let step = getStepFromPosition(
                x,
                props.resolution,
                undefined,
                xScale());
            let originalStep = step;
            let resolution = 32 / props.resolution.resolution;
            let remainder = step % resolution;
            step -= remainder;

            if (rollDragging.current) {
                let quantizedStep = quantizeStep(
                    originalStep - rollDragOffset.current, 64 / props.resolution.resolution);
                mix.dispatch(moveRollEvent(rollDragging.current, quantizedStep));
            } else if (resizing.current) {
                let difference = 
                    step - (resizingSubEvent.current.step + resizingSubEvent.current.duration);
                
                if (direction.current === RIGHT) {
                    // check that next event doesnt overlap
                    if (isRightEventInBounds(step, resizing.current, resizingSubEvent.current) &&
                        isRightSubEventInBounds(step, resizing.current, resizingSubEvent.current)) {
                        mix.resizeSequenceRoll(
                            resizing.current, resizingSubEvent.current, RIGHT, Math.floor(difference));
                    } else {
                        resizing.current= undefined;
                        resizingSubEvent.current= undefined;
                    }
                } else {
                    if (isLeftEventInBounds(step, resizing.current) &&
                        isLeftSubEventInBounds(step, resizing.current, resizingSubEvent.current)) {
                        mix.resizeSequenceRoll(
                            resizing.current, resizingSubEvent.current, LEFT, Math.floor(step));
                    } else {
                        resizing.current = undefined;
                        resizingSubEvent.current= undefined;
                    }
                }
            } else if (resizingSelection.current) {
                let selection = getState().arrangement.rowSelections[NO_SUB_SEQUENCE.id];
                if (selection !== undefined) {
                    selection.end = step;
                    let selections = {};
                    selections[NO_SUB_SEQUENCE.id] = selection;
                    props.setArrangementRowSelections(selections);
                }
            } 
        }
    }, []);

    const isLeftEventInBounds = (stepToResize, event) => {
        let previousEvent = mix.getPreviousEvent(event);
        let nextStep = mix.getNextEvent(event);

        if (previousEvent === undefined || stepToResize > previousEvent.step) {
            if (nextStep === undefined || stepToResize < nextStep.step) {
                return true;
            }
        }
        return false;
    };

    const isLeftSubEventInBounds = (stepToResize, event, subEvent) => {
        let previousEvent = event.getPreviousSubEvent(subEvent);
        let nextStep = event.getNextSubEvent(subEvent);

        if (previousEvent === undefined || stepToResize > previousEvent.step) {
            if (nextStep === undefined || stepToResize < nextStep.step) {
                return true;
            }
        }
        return false;
    };

    const isRightEventInBounds = (stepToResize, event, subEvent) => {
        let nextEvent = mix.getNextEvent(event);
        let previousEvent = mix.getPreviousEvent(event);

        if (nextEvent) {
            let nextStepAfter = mix.getNextEvent(nextEvent);
            if (nextStepAfter === undefined ||
                stepToResize < nextStepAfter.step) {
                return true;
            }
        }
        return false;
    };

    const isRightSubEventInBounds = (stepToResize, event, subEvent) => {
        if (stepToResize < subEvent.step) {
            return false;
        }
        let nextEvent = event.getNextSubEvent(subEvent);
        let previousEvent = event.getPreviousSubEvent(subEvent);

        if (nextEvent === undefined) {
            return true;
        }

        let nextStepAfter = event.getNextSubEvent(nextEvent);
        if (nextStepAfter === undefined ||
            stepToResize < nextStepAfter.step) {
            return true;
        }
        return false;
    }

    const onMouseDown = (event, subEvent, _direction) => {
        resizing.current = event;
        resizingSubEvent.current = subEvent;
        direction.current = _direction;
    };


    const selectRow = () => {
        props.setSelectedRows([NO_SUB_SEQUENCE.id]);
        props.setArrangementMode(undefined);
    };


    const onSelectionMouseDown = (e) => {
        let step = getStepFromMouseEvent(e);
        resizingSelection.current= true;
        let rowSelections = {};
        rowSelections[NO_SUB_SEQUENCE.id] = {
            start: step,
            end: step
        };
        props.setArrangementRowSelections(rowSelections);
    };

    let state = getState();

    let sequenceRollEvents = getEvents();
    let eventsToShow = [];
    if (visibleView.start !== undefined) {
        eventsToShow = 
            sequenceRollEvents.slice(visibleView.start, visibleView.end + 1);
    }
 
    let className = "pattern-row rolls-row";
    if (props.isSelected) {
        className += ' selected';
    }

    let style = {
        width: props.width
    };

    return (
        <div
          style={style}
          onClick={selectRow}
          onMouseMove={onMouseMove}
          onMouseDown={onSelectionMouseDown}
          onMouseUp={(e) => onMouseUp(undefined)}
          onMouseLeave={onMouseUp}
          className={className}>
          <RowSelection id={NO_SUB_SEQUENCE.id}/>
          <MouseHelper subSequence={NO_SUB_SEQUENCE}/>
          <div className="pattern-filler" style={{width: visibleView.preWidth}}/>
          {eventsToShow
           .filter(e => e !== undefined)
           .map(
               (event, i) =>
                   <RollComponent
                     key={event.id}
                     type={SEQUENCE_ROLL}
                     mixSession={props.mixSession}
                     resolution={props.resolution}
                     isLast={i === sequenceRollEvents.length - 1}
                     isFirst={i === 0}
                     onDragStart={(e) => onDragPatternEvent(e, event)}
                     onSelect={(event, subEvent, e) => onSelect(event, subEvent, e)}
                     onMouseUp={(e) => onMouseUp(event)}
                     onRightMouseUp={(event, subEvent) => onMouseUp(event, subEvent)}
                     onRightMouseDown={(event, subEvent) => onMouseDown(event, subEvent, RIGHT)}
                     onLeftMouseUp={(event, subEvent) => onMouseUp(event, subEvent)}
                     onLeftMouseDown={(event, subEvent) => onMouseDown(event, subEvent, LEFT)}
                     mixSession={props.mixSession}
                     sequencerEngine={props.sequencerEngine}
                     event={event}
                   />)}
        </div>);
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        setSelectedRows: p => dispatch(setArrangementSelectedRows(p)),
        setSelectedEvents: (events) => dispatch(
            setSelectedArrangementEvents(events)),
        setArrangementRowSelections: (selections) => dispatch(
            setArrangementRowSelections(selections)),
        setArrangementMode: (mode) => dispatch(
            setArrangementBottomMode(mode)),
    };
};
 
const mapStateToProps = (state, props) => {
    let mixEvents = state.arrangement.mixEvents[NO_SUB_SEQUENCE.id];
    let sequenceRollEvents = mixEvents ? mixEvents[SEQUENCE_ROLL] : [];
    return {
        isSelected: getIsSelected(state, props),
        numEvents: sequenceRollEvents === undefined ? 0 : sequenceRollEvents.length,
        resolution: DEFAULT_RESOLUTION,
        xScale: state.arrangement.xScale,
    };
};
 
export default connect(mapStateToProps, mapDispatchToProps)(RollsArrangementComponent);
